import { Link } from "react-router-dom";
import { CloudSun, Sun, Backpack, ArrowRight, CalendarCheck2 } from "lucide-react";
import { weatherLook } from "@/components/calendar/palette";

const dayName = (iso) =>
  new Date(`${iso}T12:00:00`).toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "short" });

export const TomorrowSky = ({ data, moving, onMove }) => {
  const { sky, window: dry, packing, next_outdoor: outdoor, reschedule } = data;
  const look = sky ? weatherLook(sky.code) : null;
  const Icon = look?.Icon || CloudSun;

  return (
    <>
      {sky ? (
        <div className="mt-4 flex items-center gap-4" data-testid="home-weather-sky">
          <Icon className="h-9 w-9 shrink-0" style={{ color: look?.tint }} />
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold">{look?.label}</p>
            <p className="truncate text-xs text-ink-soft">
              {dayName(data.day)} · {sky.max}° / {sky.min}° · {sky.precip}% rain
            </p>
            <p className="truncate text-xs text-ink-soft">{look?.blurb}</p>
          </div>
        </div>
      ) : (
        <p className="mt-4 text-sm text-ink-soft" data-testid="home-weather-noplace">
          Set your place in the calendar and I'll put tomorrow's sky right here.
        </p>
      )}

      {dry && (
        <p className="mt-3 inline-flex items-center gap-2 rounded-full border border-white/10 bg-page px-3 py-1.5 text-xs"
           data-testid="home-weather-window">
          <Sun className="h-3.5 w-3.5 text-amber-300" />
          Driest stretch <span className="font-semibold">{dry.from} – {dry.to}</span> · {dry.hours}h
        </p>
      )}

      {packing?.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2" data-testid="home-weather-packing">
          {packing.map((item) => (
            <span
              key={item.thing}
              title={item.why}
              className="inline-flex items-center gap-1.5 rounded-full border border-white/10 px-3 py-1.5 text-xs text-ink-soft"
            >
              <Backpack className="h-3.5 w-3.5 text-brand" /> {item.thing}
            </span>
          ))}
        </div>
      )}

      {outdoor && (
        <Link
          to="/calendar"
          data-testid="home-weather-outdoor"
          className="mt-4 flex items-center gap-2 rounded-2xl border border-white/10 bg-page px-4 py-3 text-sm transition-colors hover:border-brand/40"
        >
          <span className="min-w-0 flex-1">
            <span className="block truncate font-semibold" dir="auto">{outdoor.title}</span>
            <span className="block truncate text-xs text-ink-soft">
              Next thing outdoors · {dayName(outdoor.day)}
              {outdoor.start ? ` · ${outdoor.start}` : ""}
              {outdoor.location ? ` · ${outdoor.location}` : ""}
            </span>
          </span>
          <ArrowRight className="h-4 w-4 shrink-0 text-brand rtl:rotate-180" />
        </Link>
      )}

      {reschedule?.better && (
        <div className="mt-4 rounded-2xl border border-sky-400/40 bg-sky-400/10 p-3 text-xs text-sky-50/90"
             data-testid="home-weather-reschedule">
          <p className="flex items-center gap-2">
            <CalendarCheck2 className="h-3.5 w-3.5 text-sky-200" />
            <span className="font-semibold" dir="auto">{reschedule.title}</span>
            <span>looks rained on ({reschedule.precip}%)</span>
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <span className="text-sky-200">
              {dayName(reschedule.better.day)} is drier ({reschedule.better.precip}%)
            </span>
            <button
              data-testid="home-weather-move"
              onClick={() => onMove(reschedule)}
              disabled={moving}
              className="ms-auto inline-flex min-h-[36px] items-center gap-1.5 rounded-full bg-brand px-3.5 text-xs font-semibold text-white transition-colors hover:bg-brand-hover active:scale-95 disabled:opacity-50"
            >
              {moving ? "Moving…" : "Move it there"}
            </button>
          </div>
        </div>
      )}
    </>
  );
};
